import {useMemo, useRef, useState, useEffect, useCallback, type CSSProperties} from 'react'
import {useAppSelector} from '../store'
import {selectActiveScene} from './store/scenesSlice'
import {computeTextBlockLayout} from '../utils/textLayout'
import type {ScreenText} from '../types/textTypes'
import {carouselGapPx} from '../constants/exportPresets'

// Base width used by renderTextsToCanvas for font scaling
const FONT_BASE_WIDTH = 1290
// Reference natural width used for sprite scale=1 at FONT_BASE_WIDTH-wide frame
const SPRITE_BASE_WIDTH = 400

// Shared measuring context for justify layout — mirrors ctx.measureText in
// renderTextsToCanvas so the overlay preview matches the export pixel-wise.
let measureCtx: CanvasRenderingContext2D | null = null
const getMeasureCtx = (): CanvasRenderingContext2D => {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d')!
  return measureCtx
}

// Re-render justify measurements once webfonts finish loading (measurements
// taken before the font lands use a fallback font).
const useFontsVersion = (): number => {
  const [version, setVersion] = useState(0)
  useEffect(() => {
    const bump = () => setVersion((n) => n + 1)
    document.fonts.addEventListener('loadingdone', bump)
    return () => document.fonts.removeEventListener('loadingdone', bump)
  }, [])
  return version
}

// ── Text2DSpan — one 2D text with intra-block alignment ────────────

interface Text2DSpanProps {
  text: ScreenText
  frameWidth: number
}

const Text2DSpan = ({text: t, frameWidth}: Text2DSpanProps) => {
  const fontWeight = t.bold ? 700 : 400
  const fontStyle = t.italic ? 'italic' : 'normal'
  // Scale font size to match export rendering: (fontSize / 1290) * frameWidth
  const scaledFontSize = (t.fontSize / FONT_BASE_WIDTH) * frameWidth
  const align = t.align ?? 'center'
  const fontsVersion = useFontsVersion()

  // Justify needs manual per-line word-spacing — CSS text-align: justify does
  // not stretch lines that end in a forced break under white-space: pre.
  const justifyLayout = useMemo(() => {
    // fontsVersion is a re-measure trigger — webfont loads change widths
    void fontsVersion
    if (align !== 'justify') return null
    const ctx = getMeasureCtx()
    ctx.font = `${fontStyle} ${fontWeight} ${scaledFontSize}px "${t.fontFamily}", sans-serif`
    return computeTextBlockLayout(t.text.split('\n'), (s) => ctx.measureText(s).width)
  }, [align, t.text, t.fontFamily, fontStyle, fontWeight, scaledFontSize, fontsVersion])

  const style: CSSProperties = {
    position: 'absolute',
    left: `${t.x * 100}%`,
    top: `${t.y * 100}%`,
    transform: `translate(-50%, -50%)${t.rotation !== 0 ? ` rotate(${t.rotation}deg)` : ''}`,
    fontFamily: `"${t.fontFamily}", sans-serif`,
    fontSize: `${scaledFontSize}px`,
    fontWeight,
    fontStyle,
    color: t.color,
    // `pre` preserves explicit \n line breaks (textarea input) and spaces
    // without auto-wrapping — matches renderTextsToCanvas which only splits
    // on \n and uses the same 1.2 line-height.
    whiteSpace: 'pre',
    textAlign: align === 'justify' ? 'left' : align,
    lineHeight: 1.2,
    userSelect: 'none',
  }

  if (!justifyLayout) return <span style={style}>{t.text}</span>

  // Justify: fixed block width + one div per line with computed word-spacing.
  // Each space renders at natural + spacingDelta = gap, matching the canvas
  // renderer's word-by-word placement exactly.
  return (
    <span style={{...style, display: 'inline-block', width: `${justifyLayout.maxWidth}px`}}>
      {t.text.split('\n').map((line, i) => {
        const {spacingDelta, gap} = justifyLayout.justify[i]
        const lineStyle: CSSProperties = {
          whiteSpace: 'pre',
          textAlign: 'left',
          ...(gap !== 0 ? {wordSpacing: `${spacingDelta}px`} : {}),
        }
        return (
          <div key={i} style={lineStyle}>
            {line === '' ? ' ' : line}
          </div>
        )
      })}
    </span>
  )
}

// ── TextOverlay — renders 2D texts + 2D sprites over the full viewport ─

const TextOverlay = () => {
  const texts = useAppSelector((s) => selectActiveScene(s).texts)
  const sprites2d = useAppSelector((s) => selectActiveScene(s).sprites2d)
  const exportWidth = useAppSelector((s) => selectActiveScene(s).exportWidth)
  const exportHeight = useAppSelector((s) => selectActiveScene(s).exportHeight)
  const carouselEnabled = useAppSelector((s) => selectActiveScene(s).exportCarouselEnabled)
  const carouselCount = useAppSelector((s) => selectActiveScene(s).exportCarouselCount)
  const carouselGap = useAppSelector((s) => selectActiveScene(s).exportCarouselGap)
  const containerRef = useRef<HTMLDivElement>(null)
  const [frameWidth, setFrameWidth] = useState(1)

  // Per-slice width inside the visible carousel strip. The export render sizes
  // each text/sprite via `(scale * 400 * exportW) / 1290` where `exportW` is the
  // SLICE width (carousel renders are per-slice; see `ExportHandler.renderExport`
  // — `renderSpritesToCanvas` / `renderTextsToCanvas` always receive `exportW`,
  // not `fullWidth`). For viewport-vs-render parity we therefore need the
  // viewport equivalent of one slice: the contain-fit of the FULL carousel strip
  // (`exportWidth × N`) divided by N. In wide viewports this reduces to
  // `vpH × singleAspect` (= the old formula), but in narrow viewports it becomes
  // `vpW / N`, which the old formula got wrong (it used `vpW` directly when
  // `vpAspect < singleAspect`). For non-carousel exports `slices=1` → identical.
  const slices = carouselEnabled ? carouselCount : 1
  // Full strip width in export-pixel units, including inter-slice gutters — the
  // per-slice exports are cut from this gapped strip.
  const totalWidthUnits = exportWidth * slices + carouselGapPx(exportWidth, carouselGap) * (slices - 1)
  const fullAspect = totalWidthUnits / exportHeight

  // Compute per-slice export-frame pixel-width inside the viewport
  const updateFrameWidth = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const vpW = el.clientWidth
    const vpH = el.clientHeight
    if (vpW === 0 || vpH === 0) return
    const vpAspect = vpW / vpH
    const stripWidth = vpAspect > fullAspect ? vpH * fullAspect : vpW
    // One slice's on-screen width: strip scaled by the slice's fraction of the
    // total gapped strip. Font/sprite sizing keys to slice width (= exportW).
    setFrameWidth((stripWidth * exportWidth) / totalWidthUnits)
  }, [fullAspect, totalWidthUnits, exportWidth])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(updateFrameWidth)
    ro.observe(el)
    return () => ro.disconnect()
  }, [updateFrameWidth])

  const containerStyle: CSSProperties = useMemo(
    () => ({
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      overflow: 'hidden',
      zIndex: 6,
    }),
    [],
  )

  return (
    <div ref={containerRef} style={containerStyle}>
      {sprites2d.map((s) => {
        if (!s.imageUrl) return null
        const aspect = s.imageAspect > 0 ? s.imageAspect : 1
        // Long-side reference at SPRITE_BASE_WIDTH px in 1290-base; multiply by scale and frame ratio.
        const longSide = (s.scale * SPRITE_BASE_WIDTH * frameWidth) / FONT_BASE_WIDTH
        const renderW = aspect >= 1 ? longSide : longSide * aspect
        const renderH = renderW / aspect
        const style: CSSProperties = {
          position: 'absolute',
          left: `${s.x * 100}%`,
          top: `${s.y * 100}%`,
          width: `${renderW}px`,
          height: `${renderH}px`,
          transform: `translate(-50%, -50%)${s.rotation !== 0 ? ` rotate(${s.rotation}deg)` : ''}`,
          userSelect: 'none',
          pointerEvents: 'none',
        }
        return <img key={s.id} src={s.imageUrl} alt="" style={style} draggable={false} />
      })}
      {texts.map((t) => (
        <Text2DSpan key={t.id} text={t} frameWidth={frameWidth} />
      ))}
    </div>
  )
}

export default TextOverlay
